import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { compose } from 'redux';
import { ViewportAction } from '@sparefoot/react-core';
import { isSPMedium } from '../../../utils/breakpoints';
import { withViewportWidth } from 'decorators';

import pluralize from 'utils/pluralize';
import { withSelfStorageSchema } from 'decorators/search';

import { H3, Paragraph, Text } from 'components/core/Typography';
import { Link } from 'components/core/Link';
import { Image } from 'components/core/Image';
import { Button } from 'components/core/Button';
import { TooltipBanner } from 'components/core/TooltipBanner';
import ExpressIcon from 'components/svg/ExpressIcon';

import { FacilityImage } from 'components/facility/FacilityImage';
import { ClickToCall } from 'components/facility/ClickToCall';
import { BlueStarRating } from 'components/review/StarRating';
import { NewStackedUnits } from 'components/unit/StackedUnits';
import { LoadingFacilityCard } from 'components/facility/FacilityCard/LoadingFacilityCard';

import ChevronRight from 'assets/images/icons/chevron-right.svg';
import Close from 'assets/images/icons/close.svg';

import './NewFacilityCard.scss';
import { trackCTAClick } from '../../core/library/tracking-utils/tracking/clickTrackingUtils';

export const enhance = compose(withSelfStorageSchema, withViewportWidth);

export function NewFacilityCard({
	bottomMap,
	facility,
	baseUrl,
	facilitySelected,
	getFacilityCardPhoneNumber,
	handleFacilityDeselected,
	handleFacilitySelected,
	handlePinDeselected,
	lazyLoad,
	loading,
	miniCard,
	onVisible,
	percentVisible,
	storageType = 'storage',
	searchParams,
	selfStorageSchema,
	withSchema,
	hasOMI,
	viewportWidth,
}) {
	const handleFetchNumber = useCallback(
		() => getFacilityCardPhoneNumber(),
		[getFacilityCardPhoneNumber],
	);

	const handleDeselect = useCallback(() => {
		handleFacilityDeselected();
		handlePinDeselected();
	}, [handleFacilityDeselected, handlePinDeselected]);

	const handleSelect = useCallback(() => {
		if (!miniCard) {
			handleFacilitySelected(facility.id);
			handlePinDeselected();
		}
	}, [miniCard, handleFacilitySelected, facility.id, handlePinDeselected]);

	const handleTrackCTA = useCallback(() => {
		trackCTAClick(
			'view-all-units',
			'View all units/prices Button',
			'Compare all units at this facility',
			facility.url,
		);
	}, [facility.url]);

	if (loading) {
		return <LoadingFacilityCard />;
	}

	const isLargeView = isSPMedium(viewportWidth);
	return (
		<ViewportAction
			onVisible={onVisible}
			percentVisible={percentVisible}
		>
			<div
				className={classnames(
					'new-facility-card',
					'facility-card-loaded',
					{ selected: facility.id === facilitySelected },
					{ miniCard },
					{ bottomMap },
				)}
				role="group"
				onMouseEnter={handleSelect}
				onMouseLeave={miniCard ? undefined : handleDeselect}
				itemScope
				itemType="https://schema.org/LocalBusiness"
			>
				{miniCard && (
					<Button
						className="close-bottom-map-card"
						onClick={handleDeselect}
					>
						<Image
							src={Close}
							alt="close card"
						/>
					</Button>
				)}
				<div className="facility-head">
					<Link href={`${baseUrl}${facility.url}`}>
						<div
							className="image-wrapper"
							itemProp="image"
							itemScope
							itemType="https://schema.org/ImageObject"
						>
							{hasOMI && isLargeView && (
								<TooltipBanner
									className="facility-card-omi-banner"
									tooltipContent={
										'Move into this facility online, eliminating the need to fill out rental paperwork at the facility when you plan to move in.'
									}
									bannerText="Online Move-In Available"
									textColor="darkGray"
									bannerIcon={<ExpressIcon />}
									theme="green"
								/>
							)}
							<FacilityImage
								itemProp="url"
								src={facility.images[0]}
								alt={`Photo of ${facility.name}`}
								size="medium"
								lazyLoad={lazyLoad}
							/>
						</div>
					</Link>
					<div className="facility-details">
						<Link href={`${baseUrl}${facility.url}`}>
							<H3
								className="facility-name"
								color="black"
								last
								itemProp="name"
							>
								{facility.name}
							</H3>
							<Paragraph
								className="facility-location"
								itemProp="address"
								itemScope
								itemType="https://schema.org/PostalAddress"
							>
								{`${facility.distance} miles away ${facility.location.city} ${facility.location.state} ${facility.location.zip}`}
							</Paragraph>
						</Link>
						<div className="call-n-reviews">
							<ClickToCall
								phone={facility.phone}
								onFetchNumber={handleFetchNumber}
							/>
							{!!facility.reviews.number && (
								<span className="facility-rating">
									<BlueStarRating
										rating={facility.reviews.average}
									/>
									<Link
										className="test"
										href={`${baseUrl}${facility.url}#facility-page-user-reviews`}
									>
										<Text
											className="num-rating"
											size="tiny"
											color="blue"
											last
										>
											{facility.reviews.average}
										</Text>
									</Link>
								</span>
							)}
						</div>
					</div>
				</div>
				{hasOMI && !isLargeView && (
					<TooltipBanner
						className="facility-card-omi-banner"
						tooltipContent={
							'Move into this facility online, eliminating the need to fill out rental paperwork at the facility when you plan to move in.'
						}
						bannerText="Online Move-In Available"
						textColor="darkGray"
						bannerIcon={<ExpressIcon />}
						theme="green"
					/>
				)}
				<NewStackedUnits
					facility={facility}
					baseUrl={baseUrl}
					units={facility.units}
					storageType={storageType}
					searchParams={searchParams}
					withScarcity
				/>
				<Link
					className="compare-units"
					href={`${baseUrl}${facility.url}`}
					onClick={handleTrackCTA}
				>
					<Paragraph
						size="smallMedium"
						weight="medium"
						color="blue"
						last
					>
						Compare all{' '}
						<Text
							size="smallMedium"
							weight="extraBold"
							color="blue"
						>
							{facility.units.unitsAvailable}{' '}
							{pluralize(
								'unit',
								facility.units.unitsAvailable,
							)}{' '}
						</Text>{' '}
						at this facility{' '}
						<Image
							src={ChevronRight}
							alt=""
							aria-hidden="true"
						/>
					</Paragraph>
				</Link>
			</div>
			{withSchema && (
				<script
					key="schema"
					type="application/ld+json"
					dangerouslySetInnerHTML={{ __html: selfStorageSchema }}
				/>
			)}
		</ViewportAction>
	);
}

NewFacilityCard.propTypes = {
	bottomMap: PropTypes.bool,
	facility: PropTypes.object.isRequired,
	baseUrl: PropTypes.string,
	facilitySelected: PropTypes.number,
	getFacilityCardPhoneNumber: PropTypes.func,
	handleFacilityDeselected: PropTypes.func,
	handleFacilitySelected: PropTypes.func,
	handlePinDeselected: PropTypes.func,
	miniCard: PropTypes.bool,
	loading: PropTypes.bool,
	lazyLoad: PropTypes.bool,
	onVisible: PropTypes.func.isRequired,
	percentVisible: PropTypes.number,
	searchParams: PropTypes.object,
	selfStorageSchema: PropTypes.string,
	storageType: PropTypes.oneOf(['storage', 'vehicle']),
	withSchema: PropTypes.bool,
	hasOMI: PropTypes.bool,
	viewportWidth: PropTypes.number,
};

export default enhance(NewFacilityCard);
